import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider, DemoResetProvider } from '@trinetra/shared';
import { ThemeProvider } from './contexts/ThemeContext';
import { NodeProvider, useNode } from './contexts/NodeContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CamerasPage from './pages/CamerasPage';
import RecordingsPage from './pages/RecordingsPage';
import DiscoveryPage from './pages/DiscoveryPage';
import TurnMonitor from './pages/TurnMonitor';
import CameraConfigPage from './pages/CameraConfigPage';
import CameraDetailPage from './pages/CameraDetailPage';
import MultiSiteView from './pages/MultiSiteView';
import SiteLiveWallPage from './pages/SiteLiveWallPage';
import ModelsPage from './pages/ModelsPage';
import SystemMonitor from './pages/SystemMonitor';
import NodeMonitorPage from './pages/NodeMonitorPage';
import AlertConfigPage from './pages/AlertConfigPage';
import CameraStreamPage from './pages/CameraStreamPage';
import HeatmapPage from './pages/HeatmapPage';
import ModelAssignmentPage from './pages/ModelAssignmentPage';
import GroupCameraMapPage from './pages/GroupCameraMapPage';
import AnalyticsConfigPage from './pages/AnalyticsConfigPage';
import AlertsPage from './pages/AlertsPage';
import AlertDetailPage from './pages/AlertDetailPage';
import AccessControlPage from './pages/AccessControlPage';
import SystemAlertsPage from './pages/SystemAlertsPage';
import AlertBoardPage from './pages/AlertBoardPage';
import AttendancePage from './pages/AttendancePage';
import ReportPage from './pages/ReportPage';
import EventDetailPage from './pages/EventDetailPage';
import SiteDashboardPage from './pages/SiteDashboardPage';
import UserManagementPage from './pages/UserManagementPage';
import AllSitesAlertBoardPage from './pages/AllSitesAlertBoardPage';
import { DEFAULT_ATTENDANCE_SLUG, DEFAULT_REPORT_SLUG } from '@trinetra/shared';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// function AdminRoute({ children }: { children: React.ReactNode }) {
//   const { user } = useAuth();
//   if (user?.role !== 'admin') {
//     return <Navigate to="/" replace />;
//   }
//   return <>{children}</>;
// }

/**
 * `anyPermission` is for a page that merges two RBAC surfaces (the alert board is the
 * honeycomb AND the map): entry needs either key, and the page decides what to show.
 */
function PermissionRoute({
  permission,
  anyPermission,
  children,
}: {
  permission?: string;
  anyPermission?: string[];
  children: React.ReactNode;
}) {
  const { hasPermission } = useAuth();
  const allowed = anyPermission
    ? anyPermission.some(hasPermission)
    : !permission || hasPermission(permission);
  if (!allowed) {
    // "/" is the always-accessible global Dashboard, so it's a safe landing spot.
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Resolves the Site Health menu entry to the scoped site's monitor page. All Sites has no single
 * site to show health for, so it falls back to the estate dashboard, which is where that
 * question is answered at that scope.
 */
function SiteHealthRedirect() {
  const { selectedNode } = useNode();
  return <Navigate to={selectedNode ? `/node-monitor/${selectedNode.nodeId}` : '/'} replace />;
}

/** Feeds the app's hasPermission into the shared PermissionsProvider for shared pages. */
function PermissionsBridge({ children }: { children: React.ReactNode }) {
  const { hasPermission, user } = useAuth();
  return <PermissionsProvider hasPermission={hasPermission} isSuperAdmin={user?.role === 'super_admin'}>{children}</PermissionsProvider>;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PermissionsBridge>
          <NodeProvider>
          {/* Above the routes: the demo banner lives in the title bar, so its state has to
              outlive any one page. Empty unless a node announces a wipe cycle. */}
          <DemoResetProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/stream/:nodeId/:cameraId" element={<ProtectedRoute><CameraStreamPage /></ProtectedRoute>} />

              {/* All protected routes use the Layout shell */}
              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                {/* ── GLOBAL ── the aggregate dashboard is the landing page, so it
                    no longer needs a permission-gated root with a fallback. */}
                <Route path="/" element={<Dashboard />} />
                {/* Both all-sites surfaces sit under `/all-sites/` because their per-site twins
                    own the bare paths (`/live-wall`, `/alert-board`) and carry the same menu
                    names — the scope segment is what keeps one name from meaning two pages. */}
                <Route path="/all-sites/live-wall" element={<PermissionRoute permission="multi-site.view"><MultiSiteView /></PermissionRoute>} />
                {/* Cross-site alert board — needs both scopes, so the guards nest. */}
                <Route
                  path="/all-sites/alert-board"
                  element={
                    <PermissionRoute permission="multi-site.view">
                      <PermissionRoute permission="alerts.view">
                        <AllSitesAlertBoardPage />
                      </PermissionRoute>
                    </PermissionRoute>
                  }
                />

                {/* ── Live Wall for ONE site ── */}
                {/* Not the same page as /all-sites/live-wall above: that one spans sites off
                    cloud-stored layouts, this one shows the SELECTED site's own layouts, read
                    from the node through the tunnel — the very grids that site's local
                    dashboard shows, so the two products never drift. */}
                <Route path="/live-wall" element={<PermissionRoute permission="live-wall.view"><SiteLiveWallPage /></PermissionRoute>} />

                {/* ── Alerts ── */}
                {/* One board, two layouts behind a toolbar toggle — so both paths render the
                    same page and the URL just says which layout it opens on. The guard admits
                    anyone entitled to EITHER layout; the page itself shows only the ones their
                    keys allow (and rewrites the URL to match), so `map.view` alone still can't
                    see the honeycomb and `alerts.view` alone still can't see the plan. */}
                <Route path="/alert-board" element={<PermissionRoute anyPermission={['alerts.view', 'map.view']}><AlertBoardPage /></PermissionRoute>} />
                <Route path="/alert-board/map" element={<PermissionRoute anyPermission={['alerts.view', 'map.view']}><AlertBoardPage /></PermissionRoute>} />
                <Route path="/alerts" element={<PermissionRoute permission="alerts.view"><AlertsPage /></PermissionRoute>} />
                <Route path="/alerts/:id" element={<PermissionRoute permission="alerts.view"><AlertDetailPage /></PermissionRoute>} />

                {/* The selected site's own summary — the per-site twin of the all-sites
                    dashboard at "/", and the same cards the local product shows. Unguarded:
                    each card gates itself on the key that owns its data. */}
                <Route path="/site-dashboard" element={<SiteDashboardPage />} />

                {/* ── Cameras ── */}
                <Route path="/cameras" element={<PermissionRoute permission="cameras.view"><CamerasPage /></PermissionRoute>} />
                <Route path="/recordings" element={<PermissionRoute permission="recordings.view"><RecordingsPage /></PermissionRoute>} />

                {/* ── Reports — one page per AI model ── */}
                <Route path="/reports" element={<Navigate to={`/reports/${DEFAULT_REPORT_SLUG}`} replace />} />
                {/* Heatmap is not an event model (it reports metrics, never alerts), so it gets
                    its own page rather than a slug on the event report. A static segment
                    outranks `:model`, so order here is for readability only.

                    The SAME page as the per-camera route below: there is no separate list of
                    cameras any more, the cameras are the rail beside the picture. Without a
                    camera in the path it opens on the busiest one and writes it back. */}
                <Route path="/reports/heatmap" element={<PermissionRoute permission="ai_events.view"><HeatmapPage /></PermissionRoute>} />
                <Route path="/reports/:model" element={<PermissionRoute permission="ai_events.view"><ReportPage /></PermissionRoute>} />
                {/* One event, as a page rather than a modal — the link that survives being
                    pasted into a ticket. Same permission as the report it came from. */}
                <Route path="/events/:model/:eventId" element={<PermissionRoute permission="ai_events.view"><EventDetailPage /></PermissionRoute>} />

                {/* ── Camera Management ── */}
                <Route path="/camera-management" element={<PermissionRoute permission="cameras.view"><CamerasPage mode="manage" /></PermissionRoute>} />
                <Route path="/nodes/:nodeId/cameras/:cameraId" element={<PermissionRoute permission="cameras.view"><CameraDetailPage /></PermissionRoute>} />
                <Route path="/nodes/:nodeId/cameras/:cameraId/config" element={<PermissionRoute permission="cameras.config"><CameraConfigPage /></PermissionRoute>} />
                <Route path="/nodes/:nodeId/cameras/:cameraId/heatmap" element={<PermissionRoute permission="cameras.view"><HeatmapPage /></PermissionRoute>} />
                <Route path="/camera-groups" element={<PermissionRoute permission="groups.view"><GroupCameraMapPage /></PermissionRoute>} />
                <Route path="/access-control" element={<PermissionRoute permission="users.edit"><AccessControlPage /></PermissionRoute>} />
                <Route path="/discovery" element={<PermissionRoute permission="discovery.view"><DiscoveryPage /></PermissionRoute>} />

                {/* ── AI Models ── */}
                <Route path="/models" element={<PermissionRoute permission="ai-models.view"><ModelsPage /></PermissionRoute>} />
                <Route path="/model-assignments" element={<PermissionRoute permission="ai-models.assign"><ModelAssignmentPage /></PermissionRoute>} />
                <Route path="/alert-rules" element={<PermissionRoute permission="alerts.config"><AnalyticsConfigPage /></PermissionRoute>} />

                {/* ── Attendance — one page per surface ── */}
                <Route path="/attendance" element={<Navigate to={`/attendance/${DEFAULT_ATTENDANCE_SLUG}`} replace />} />
                <Route path="/attendance/:tab" element={<PermissionRoute permission="attendance.view"><AttendancePage /></PermissionRoute>} />

                {/* ── Header user menu ── */}
                <Route path="/platform-health" element={<PermissionRoute permission="monitoring.view"><SystemMonitor /></PermissionRoute>} />
                <Route path="/turn-monitor" element={<PermissionRoute permission="turn.view"><TurnMonitor /></PermissionRoute>} />
                <Route path="/system-alerts" element={<PermissionRoute permission="system_alert.view"><SystemAlertsPage /></PermissionRoute>} />
                <Route path="/user-management" element={<UserManagementPage />} />

                {/* Site Health's menu entry. The page itself takes its node from the URL, which
                    is exactly why it used to be kept out of the sidebar — it could name a
                    different site than the picker did. With scope explicit and app-wide there is
                    one answer, so this resolves the menu's static path to the scoped node the
                    same way /reports and /attendance resolve to their default slugs. */}
                <Route path="/system-health" element={<PermissionRoute permission="monitoring.view"><SiteHealthRedirect /></PermissionRoute>} />

                {/* ── Not in the nav, reached from other pages ── */}
                <Route path="/node-monitor/:nodeId" element={<PermissionRoute permission="monitoring.view"><NodeMonitorPage /></PermissionRoute>} />
                <Route path="/alert-config" element={<PermissionRoute permission="alerts.view"><AlertConfigPage /></PermissionRoute>} />

                {/* ── Redirects from the pre-restructure paths ── */}
                {/* Renamed so the URL says what the menu says: a path that named the LAYOUT
                    (`/hexagon-alerts`, `/map-view`) or the implementation
                    (`/group-camera-map`, `/analytics-config`, `/multi-site-*`) now names the
                    page. The old ones stay as redirects because they are in bookmarks, in
                    shared links and in the alert emails the cloud sends. */}
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/hexagon-alerts" element={<Navigate to="/alert-board" replace />} />
                <Route path="/map-view" element={<Navigate to="/alert-board/map" replace />} />
                <Route path="/analytics-config" element={<Navigate to="/alert-rules" replace />} />
                <Route path="/group-camera-map" element={<Navigate to="/camera-groups" replace />} />
                <Route path="/multi-site-view" element={<Navigate to="/all-sites/live-wall" replace />} />
                <Route path="/multi-site-alerts" element={<Navigate to="/all-sites/alert-board" replace />} />
                <Route path="/system-monitor" element={<Navigate to="/platform-health" replace />} />
                <Route path="/site-health" element={<Navigate to="/system-health" replace />} />
                <Route path="/groups" element={<Navigate to="/camera-groups" replace />} />
                <Route path="/realtime-monitoring" element={<Navigate to="/alert-board" replace />} />
                <Route path="/alert-investigation" element={<Navigate to={`/reports/${DEFAULT_REPORT_SLUG}`} replace />} />
                <Route path="/users" element={<Navigate to="/user-management?tab=users" replace />} />
                <Route path="/roles" element={<Navigate to="/user-management?tab=roles" replace />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </DemoResetProvider>
          </NodeProvider>
          </PermissionsBridge>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
